import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import Dropdown from "../../components/Dropdown";
import {
  createStudent,
  downloadStudentInfo,
} from "../../services/StudentServices";
import {
  getAllEducationLevels,
  getEducationLevelSubclass,
} from "../../services/educationLevelServices";
import { getAllDiscounts } from "../../services/schoolDiscountsServices";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import closeIconWhite from "../../assets/white_close_icon.png";
import downloadIcon from "../../assets/download_light.png";
import CopyButton from "../../components/CopyButton";
import HotdropdownV2 from "../../components/HotdropdownV2";
import CollapsibleCard, {
  CARD_CONTENT_TYPES,
} from "../attendance-system/CollapsibleCard";
import SectionDivider from "../../components/SectionDivider";

const AddStudentModal = ({
  isOpen,
  onClose,
  onStudentCreated,
  setPagination,
}) => {
  const { t } = useTranslation("general");

  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [message, setMessage] = useState("");
  const [educationLevels, setEducationLevels] = useState([]);
  const [discountType, setDiscountType] = useState("percentage");
  const [showStudentIdModal, setShowStudentIdModal] = useState(false);
  const [studentCode, setStudentCode] = useState("");
  const [studentId, setStudentId] = useState("");
  const [subclasses, setSubclasses] = useState([]);

  const [newStudentFields, setNewStudentFields] = useState({
    fullName: "",
    academicLevel: {
      title: "",
      value: "",
      price: 0,
    },
    subclass: {
      id: "",
      name: "",
    },
    source: "newStudent",
    discount: {
      title: "",
      value: "",
    },
    discountedAmount: 0,
    phoneNumber1: "",
    phoneNumber2: "",
    studentNotes: "",
    // Student extra info
    dayOfBirth: "",
    monthOfBirth: "",
    yearOfBirth: "",
    unifiedCardNumber: "",
    civilStatusIdNumber: "",
    mahala: "",
    zoqaq: "",
    dar: "",
    nearestPoint: "",
    numberOfFamilyNumber: "",
    studentOrderAmongFamily: "",
    livingWith: "",
    // Father info
    fatherName: "",
    fatherAlive: "",
    fatherUnifiedCardNumber: "",
    fatherCivilStatusIdNumber: "",
    fatherNationalityCertificateNumber: "",
    fatherDayOfBirth: "",
    fatherMonthOfBirth: "",
    fatherYearOfBirth: "",
    fatherPhoneNumber: "",
    fatherAcademicAchievement: "",
    fatherCurrentJob: "",
    // Mother info
    motherName: "",
    motherAlive: "",
    motherDayOfBirth: "",
    motherMonthOfBirth: "",
    motherYearOfBirth: "",
    motherPhoneNumber: "",
    motherAcademicAchievement: "",
    motherCurrentJob: "",
  });

  const fetchEducationLevels = async (
    page = 1,
    limit = 5,
    isDropDown = false,
    searchTerm
  ) => {
    try {
      const response = await getAllEducationLevels({
        page,
        limit,
        search: searchTerm, // ✅ Add this line
      });

      const transformedData = (response.data.classes || []).map((level) => ({
        title: level.name,
        value: level.id,
        price: level.price,
      }));

      // ✅ Handle pagination properly
      if (page === 1) {
        setEducationLevels(transformedData);
      } else {
        setEducationLevels((prev) => [...prev, ...transformedData]);
      }

      // ✅ Return the expected structure
      return {
        status: response.status || 200,
        data: {
          classes: transformedData,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        },
      };
    } catch (error) {
      console.error("Error fetching education levels:", error);
      setEducationLevels([]);

      // ✅ Return error structure
      return {
        status: 500,
        data: { classes: [], totalPages: 1, totalRecords: 0 },
      };
    }
  };

  const fetchSubclasses = async (educationLevelId) => {
    try {
      const response = await getEducationLevelSubclass(educationLevelId);
      const formattedSubclasses = response.data.subClasses.map((subclass) => ({
        title: `${subclass.name} (${t("students")}: ${subclass.studentCount})`,
        value: subclass.id,
        name: subclass.name,
      }));
      setSubclasses(formattedSubclasses);
    } catch (error) {
      console.error("Error fetching subclasses:", error);
      setSubclasses([]);
    }
  };

  const handleNewStudentInputChange = (e, fieldName) => {
    let value = e.target.value;
    setNewStudentFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
    }));
  };

  const parseOptionalInt = (value) => {
    return value ? parseInt(value) : null;
  };

  // Function to download a student information as docx
  const handleDownloadStudentInfo = async (student) => {
    try {
      const response = await downloadStudentInfo(student.id);

      // Get content type from response headers
      const contentType =
        response.headers["content-type"] ||
        response.headers["Content-Type"] ||
        "application/octet-stream";

      // Determine file extension based on content type
      let extension = ".pdf"; // default
      if (contentType.includes("wordprocessingml.document")) {
        extension = ".docx";
      } else if (contentType.includes("application/pdf")) {
        extension = ".pdf";
      }

      // Create blob with correct content type
      const blob = new Blob([response.data], { type: contentType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Set filename with correct extension
      let filename = `${student.studentCode}${extension}`;

      // Try to get filename from headers
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const matches = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      // Ensure correct extension
      if (!filename.toLowerCase().endsWith(extension)) {
        const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
        filename = `${nameWithoutExt}${extension}`;
      }

      link.setAttribute("download", filename);

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      setError(true);
      setErrorMessage("downloadFileError");
    }
  };

  const [requiredFieldsErrors, setRequiredFieldsErrors] = useState({
    fullName: false,
    academicLevel: false,
    phoneNumber1: false,
    subclass: false,
  });

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    try {
      if (!newStudentFields.fullName) {
        setRequiredFieldsErrors((prev) => ({
          ...prev,
          fullName: true,
        }));
        setError(true);
        setErrorMessage(t("inputsCheckError"));
        return;
      }
      if (!newStudentFields.academicLevel.value) {
        setRequiredFieldsErrors((prev) => ({
          ...prev,
          academicLevel: true,
        }));
        return;
      }
      if (!newStudentFields.subclass.id) {
        setRequiredFieldsErrors((prev) => ({
          ...prev,
          subclass: true,
        }));
        return;
      }
      if (!newStudentFields.phoneNumber1) {
        setRequiredFieldsErrors((prev) => ({
          ...prev,
          phoneNumber1: true,
        }));
        return;
      }

      setRequiredFieldsErrors({
        fullName: false,
        academicLevel: false,
        phoneNumber1: false,
        supClass: false,
      });

      const newStudentData = {
        fullName: newStudentFields.fullName,
        source: newStudentFields.source,
        notes: newStudentFields.studentNotes,
        discountedAmount: newStudentFields.discount.value
          ? 0
          : parseFloat(newStudentFields.discountedAmount) || 0,
        schoolClassId: newStudentFields.academicLevel.value,
        schoolSubClassId: newStudentFields.subclass.id,
        phoneNumber1: newStudentFields.phoneNumber1,
        phoneNumber2: newStudentFields.phoneNumber2 || null,
        schoolDiscountId: newStudentFields.discount.value || null,
        isPercentage: discountType === "percentage",
        // Student extra info
        dayOfBirth: parseOptionalInt(newStudentFields.dayOfBirth),
        monthOfBirth: parseOptionalInt(newStudentFields.monthOfBirth),
        yearOfBirth: parseOptionalInt(newStudentFields.yearOfBirth),
        unifiedCardNumber: newStudentFields.unifiedCardNumber || null,
        civilStatusIdNumber: newStudentFields.civilStatusIdNumber || null,
        mahala: newStudentFields.mahala || null,
        zoqaq: newStudentFields.zoqaq || null,
        dar: newStudentFields.dar || null,
        nearestPoint: newStudentFields.nearestPoint || null,
        numberOfFamilyNumber: parseOptionalInt(
          newStudentFields.numberOfFamilyNumber
        ),
        studentOrderAmongFamily: parseOptionalInt(
          newStudentFields.studentOrderAmongFamily
        ),
        livingWith: newStudentFields.livingWith || null,
        // Father info
        fatherName: newStudentFields.fatherName || null,
        fatherAlive: newStudentFields.fatherAlive || null,
        fatherUnifiedCardNumber:
          newStudentFields.fatherUnifiedCardNumber || null,
        fatherCivilStatusIdNumber:
          newStudentFields.fatherCivilStatusIdNumber || null,
        fatherNationalityCertificateNumber:
          newStudentFields.fatherNationalityCertificateNumber || null,
        fatherDayOfBirth: parseOptionalInt(newStudentFields.fatherDayOfBirth),
        fatherMonthOfBirth: parseOptionalInt(
          newStudentFields.fatherMonthOfBirth
        ),
        fatherYearOfBirth: parseOptionalInt(newStudentFields.fatherYearOfBirth),
        fatherPhoneNumber: newStudentFields.fatherPhoneNumber || null,
        fatherAcademicAchievement:
          newStudentFields.fatherAcademicAchievement || null,
        fatherCurrentJob: newStudentFields.fatherCurrentJob || null,
        // Mother info
        motherName: newStudentFields.motherName || null,
        motherAlive: newStudentFields.motherAlive || null,
        motherDayOfBirth: parseOptionalInt(newStudentFields.motherDayOfBirth),
        motherMonthOfBirth: parseOptionalInt(
          newStudentFields.motherMonthOfBirth
        ),
        motherYearOfBirth: parseOptionalInt(newStudentFields.motherYearOfBirth),
        motherPhoneNumber: newStudentFields.motherPhoneNumber || null,
        motherAcademicAchievement:
          newStudentFields.motherAcademicAchievement || null,
        motherCurrentJob: newStudentFields.motherCurrentJob || null,
      };

      setLoadingModal(true);
      const response = await createStudent(newStudentData);

      setMessage(response.data.message);

      if (setPagination) {
        setPagination((prev) => ({
          ...prev,
          totalRecords: prev.totalRecords + 1,
        }));
      }

      if (response.status === 201) {
        setError(false);
        setErrorMessage("");
        setStudentCode(() => response.data.student.studentCode);
        setStudentId(() => response.data.student.id);
        setShowStudentIdModal(() => true);

        // Reset form
        setNewStudentFields({
          fullName: "",
          academicLevel: {
            title: "",
            value: "",
            price: 0,
          },
          subclass: {
            id: "",
            name: "",
          },
          source: "newStudent",
          discount: {
            title: "",
            value: "",
          },
          discountedAmount: 0,
          phoneNumber1: "",
          phoneNumber2: "",
          studentNotes: "",
          // Student extra info
          dayOfBirth: "",
          monthOfBirth: "",
          yearOfBirth: "",
          unifiedCardNumber: "",
          civilStatusIdNumber: "",
          mahala: "",
          zoqaq: "",
          dar: "",
          nearestPoint: "",
          numberOfFamilyNumber: "",
          studentOrderAmongFamily: "",
          livingWith: "",
          // Father info
          fatherName: "",
          fatherAlive: "",
          fatherUnifiedCardNumber: "",
          fatherCivilStatusIdNumber: "",
          fatherNationalityCertificateNumber: "",
          fatherDayOfBirth: "",
          fatherMonthOfBirth: "",
          fatherYearOfBirth: "",
          fatherPhoneNumber: "",
          fatherAcademicAchievement: "",
          fatherCurrentJob: "",
          // Mother info
          motherName: "",
          motherAlive: "",
          motherDayOfBirth: "",
          motherMonthOfBirth: "",
          motherYearOfBirth: "",
          motherPhoneNumber: "",
          motherAcademicAchievement: "",
          motherCurrentJob: "",
        });

        // Call parent callback to refresh data
        if (onStudentCreated) {
          onStudentCreated();
        }
      }

      setError(false);
      setErrorMessage("");
    } catch (error) {
      setError(true);
      setErrorMessage(t("creatingStudentError"));
    } finally {
      setLoadingModal(false);
    }
  };

  const handleCancelAddStudent = () => {
    setError(false);
    setErrorMessage("");
    setRequiredFieldsErrors({
      fullName: false,
      academicLevel: false,
      phoneNumber1: false,
      supClass: false,
    });

    // Reset form
    setNewStudentFields({
      fullName: "",
      academicLevel: {
        title: "",
        value: "",
        price: 0,
      },
      source: "newStudent",
      discount: {
        title: "",
        value: "",
      },
      discountedAmount: 0,
      phoneNumber1: "",
      phoneNumber2: "",
      studentNotes: "",
      // Student extra info
      dayOfBirth: "",
      monthOfBirth: "",
      yearOfBirth: "",
      unifiedCardNumber: "",
      civilStatusIdNumber: "",
      mahala: "",
      zoqaq: "",
      dar: "",
      nearestPoint: "",
      numberOfFamilyNumber: "",
      studentOrderAmongFamily: "",
      livingWith: "",
      // Father info
      fatherName: "",
      fatherAlive: "",
      fatherUnifiedCardNumber: "",
      fatherCivilStatusIdNumber: "",
      fatherNationalityCertificateNumber: "",
      fatherDayOfBirth: "",
      fatherMonthOfBirth: "",
      fatherYearOfBirth: "",
      fatherPhoneNumber: "",
      fatherAcademicAchievement: "",
      fatherCurrentJob: "",
      // Mother info
      motherName: "",
      motherAlive: "",
      motherDayOfBirth: "",
      motherMonthOfBirth: "",
      motherYearOfBirth: "",
      motherPhoneNumber: "",
      motherAcademicAchievement: "",
      motherCurrentJob: "",
    });
    setSubclasses([]);

    onClose();
  };

  const handleCloseAllModels = () => {
    setShowStudentIdModal(false);
    setRequiredFieldsErrors({
      fullName: false,
      academicLevel: false,
      phoneNumber1: false,
      supClass: false,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={t("createStudent")}
      onClose={handleCancelAddStudent}
      error={errorMessage}
      footer={
        <>
          {loadingModal ? <Loader /> : null}
          <section className="edit-user-actions">
            <CustomButton
              title={t("confirm")}
              onClick={handleCreateStudent}
              icon={saveIcon}
              iconSize={16}
              disabled={loadingModal}
            />
            <CustomButton
              title={t("cancel")}
              onClick={handleCancelAddStudent}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
          </section>
        </>
      }
    >
      <form onSubmit={handleCreateStudent}>
        <div>
          {error && (
            <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center">
              <ErrorBox>{errorMessage}</ErrorBox>
            </div>
          )}
          <div className="edit-user-entity">
            <span>{t("studentName")}: </span>
            <CustomInput
              name="fullName"
              value={newStudentFields.fullName}
              onChange={(e) => handleNewStudentInputChange(e, "fullName")}
              errorText={
                requiredFieldsErrors.fullName
                  ? t("inputFieldCannotBeEmpty")
                  : ""
              }
            />
            <span>{t("academicLevel")}: </span>
            {requiredFieldsErrors.academicLevel && (
              <div className="error-message">
                {t("inputFieldCannotBeEmpty")}
              </div>
            )}

            <HotdropdownV2
              initOptions={[...educationLevels]}
              selected={newStudentFields.academicLevel.title}
              setSelected={(selected) => {
                setRequiredFieldsErrors((prev) => ({
                  ...prev,
                  academicLevel: false,
                }));
                setNewStudentFields((prev) => ({
                  ...prev,
                  academicLevel: {
                    title: selected.title,
                    value: selected.value,
                    price: selected.price,
                  },

                  subclass: {
                    id: "",
                    name: "",
                  },
                }));

                if (selected.value) {
                  fetchSubclasses(selected.value);
                } else {
                  setSubclasses([]);
                }
              }}
              page="students"
              getData={(page, limit, isDropDown, query) =>
                fetchEducationLevels(page, limit, isDropDown, query)
              }
              optionsName="classes"
              thick
              key="academic-level-dropdown"
            />

            {newStudentFields.academicLevel.title && (
              <>
                <span>{t("subclass")}: </span>
                {requiredFieldsErrors.subclass && (
                  <div className="error-message">
                    {t("inputFieldCannotBeEmpty")}
                  </div>
                )}
                <Dropdown
                  options={subclasses}
                  selected={{
                    title: newStudentFields.subclass.name
                      ? subclasses.find(
                          (sub) => sub.value === newStudentFields.subclass.id
                        )?.title || ""
                      : "",
                    value: newStudentFields.subclass.id,
                  }}
                  setSelected={(selected) => {
                    setRequiredFieldsErrors((prev) => ({
                      ...prev,
                      subclass: false,
                    }));

                    setNewStudentFields((prev) => ({
                      ...prev,
                      subclass: {
                        id: selected.value,
                        name: selected.name,
                      },
                    }));
                  }}
                  thick
                />
              </>
            )}

            <span>{t("source")}: </span>
            <Dropdown
              options={[
                { title: "kg2", value: "kg2" },
                { title: "newStudent", value: "newStudent" },
                { title: "transition", value: "transition" },
              ]}
              selected={{
                title: newStudentFields.source,
                value: newStudentFields.source,
              }}
              setSelected={(selected) => {
                handleNewStudentInputChange(
                  {
                    target: {
                      value: selected.value,
                    },
                  },
                  "source"
                );
              }}
              thick
            />

            <span>{t("phoneNumber")}: </span>
            <CustomInput
              name="phoneNumber1"
              value={newStudentFields.phoneNumber1}
              onChange={(e) => handleNewStudentInputChange(e, "phoneNumber1")}
              errorText={
                requiredFieldsErrors.phoneNumber1
                  ? t("inputFieldCannotBeEmpty")
                  : ""
              }
            />

            <span>
              {t("secondPhoneNumber")} ({t("optional")}):{" "}
            </span>
            <CustomInput
              name="phoneNumber2"
              value={newStudentFields.phoneNumber2}
              onChange={(e) => handleNewStudentInputChange(e, "phoneNumber2")}
            />

            <span>{t("notes")}:</span>
            <CustomInput
              name="studentNotes"
              value={newStudentFields.studentNotes}
              onChange={(e) => {
                handleNewStudentInputChange(e, "studentNotes");
              }}
              id="notesInput"
              type="textarea"
              flex
            />

            {/* MORE STUDENT INFORMATION */}

            <CollapsibleCard
              title={t("extraStudentInfo")}
              body={
                <div className="o-column o-column--gap-size_700">
                  {/* STUDENT SECTION */}
                  <section className="o-column o-column--gap-size_500">
                    <div>
                      <SectionDivider title={"studentInfo"} />
                      {/* Birthdate */}
                      <div className="o-column o-column--gap-size_400">
                        <label className="t-bold_bold">{t("birth")}: </label>
                        <div className="o-row o-row--gap-size_500">
                          <div>
                            <label>{t("day")}:</label>
                            <CustomInput
                              type="number"
                              value={newStudentFields.dayOfBirth}
                              onChange={(e) =>
                                handleNewStudentInputChange(e, "dayOfBirth")
                              }
                            />
                          </div>
                          <div>
                            <label>{t("month")}:</label>
                            <CustomInput
                              type="number"
                              value={newStudentFields.monthOfBirth}
                              onChange={(e) =>
                                handleNewStudentInputChange(e, "monthOfBirth")
                              }
                            />
                          </div>
                          <div>
                            <label>{t("year")}:</label>
                            <CustomInput
                              type="number"
                              value={newStudentFields.yearOfBirth}
                              onChange={(e) =>
                                handleNewStudentInputChange(e, "yearOfBirth")
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ID */}
                    <div>
                      <label>{t("unifiedIdNumber")}:</label>
                      <CustomInput
                        type="text"
                        value={newStudentFields.unifiedCardNumber}
                        onChange={(e) =>
                          handleNewStudentInputChange(e, "unifiedCardNumber")
                        }
                      />

                      <p className="t-bold_bold">{t("noUnifiedIdNumber")}</p>
                      <label>{t("civilStatusIdNumber")}:</label>
                      <CustomInput
                        type="text"
                        value={newStudentFields.civilStatusIdNumber}
                        onChange={(e) =>
                          handleNewStudentInputChange(e, "civilStatusIdNumber")
                        }
                      />
                    </div>

                    {/* ADDRESS */}
                    <div className="o-column o-column--gap-size_400">
                      {/* CURRENT ADDRESS */}
                      <label className="t-bold_bold">
                        {t("currentAddress")}:
                      </label>
                      <div className="o-row o-row--gap-size_500">
                        <div>
                          <label>{t("mahala")}:</label>
                          <CustomInput
                            type="text"
                            value={newStudentFields.mahala}
                            onChange={(e) =>
                              handleNewStudentInputChange(e, "mahala")
                            }
                          />
                        </div>
                        <div>
                          <label>{t("zoqaq")}:</label>
                          <CustomInput
                            type="text"
                            value={newStudentFields.zoqaq}
                            onChange={(e) =>
                              handleNewStudentInputChange(e, "zoqaq")
                            }
                          />
                        </div>
                        <div>
                          <label>{t("dar")}:</label>
                          <CustomInput
                            type="text"
                            value={newStudentFields.dar}
                            onChange={(e) =>
                              handleNewStudentInputChange(e, "dar")
                            }
                          />
                        </div>
                      </div>

                      {/* CLOSEST POINT */}
                      <div>
                        <label>{t("nearestLandMark")}:</label>
                        <CustomInput
                          type="text"
                          value={newStudentFields.nearestPoint}
                          onChange={(e) =>
                            handleNewStudentInputChange(e, "nearestPoint")
                          }
                        />
                      </div>
                    </div>

                    {/* FAMILY  */}
                    <div className="o-column o-column--gap-size_400">
                      <label className="t-bold_bold">{t("family")}:</label>
                      <div className="o-row o-row--gap-size_400">
                        <div>
                          <label>{t("numberOfFamilyMembers")}:</label>
                          <CustomInput
                            type="number"
                            value={newStudentFields.numberOfFamilyNumber}
                            onChange={(e) =>
                              handleNewStudentInputChange(
                                e,
                                "numberOfFamilyNumber"
                              )
                            }
                          />
                        </div>
                        <div>
                          <label>{t("positionAmoungSiblings")}:</label>
                          <CustomInput
                            type="number"
                            value={newStudentFields.studentOrderAmongFamily}
                            onChange={(e) =>
                              handleNewStudentInputChange(
                                e,
                                "studentOrderAmongFamily"
                              )
                            }
                          />
                        </div>
                        <div>
                          <label>{t("livesWith")}</label>
                          <CustomInput
                            type="text"
                            value={newStudentFields.livingWith}
                            onChange={(e) =>
                              handleNewStudentInputChange(e, "livingWith")
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* FATHER SECTION */}
                  <section className="o-column o-column--gap-size_500">
                    <div>
                      <SectionDivider title={"fatherInfo"} />
                      {/* Father Name */}
                      <div>
                        <label>{t("fatherFullName")}:</label>
                        <CustomInput
                          type="text"
                          value={newStudentFields.fatherName}
                          onChange={(e) =>
                            handleNewStudentInputChange(e, "fatherName")
                          }
                        />
                      </div>
                    </div>

                    {/* Father Is Alive */}
                    <div>
                      <label>{t("fatherIsAlive")}:</label>
                      <Dropdown
                        options={[
                          { title: t("yes"), value: true },
                          { title: t("no"), value: false },
                        ]}
                        selected={{
                          title:
                            newStudentFields.fatherAlive === true
                              ? t("yes")
                              : newStudentFields.fatherAlive === false
                              ? t("no")
                              : "",
                          value: newStudentFields.fatherAlive,
                        }}
                        setSelected={(selected) => {
                          handleNewStudentInputChange(
                            {
                              target: {
                                value: selected.value,
                              },
                            },
                            "fatherAlive"
                          );
                        }}
                      />
                    </div>

                    {/* Father ID */}
                    <div className="o-column o-column--gap-size_400">
                      <div>
                        <label>{t("unifiedIdNumber")}:</label>
                        <CustomInput
                          type="text"
                          value={newStudentFields.fatherUnifiedCardNumber}
                          onChange={(e) =>
                            handleNewStudentInputChange(
                              e,
                              "fatherUnifiedCardNumber"
                            )
                          }
                        />
                      </div>

                      <p className="t-bold_bold">{t("noUnifiedIdNumber")}</p>

                      <div>
                        <label>{t("civilStatusIdNumber")}:</label>
                        <CustomInput
                          type="text"
                          value={newStudentFields.fatherCivilStatusIdNumber}
                          onChange={(e) =>
                            handleNewStudentInputChange(
                              e,
                              "fatherCivilStatusIdNumber"
                            )
                          }
                        />
                      </div>

                      <div>
                        <label>{t("idCertificateNumber")}:</label>
                        <CustomInput
                          type="text"
                          value={
                            newStudentFields.fatherNationalityCertificateNumber
                          }
                          onChange={(e) =>
                            handleNewStudentInputChange(
                              e,
                              "fatherNationalityCertificateNumber"
                            )
                          }
                        />
                      </div>
                    </div>

                    {/* Father Birthdate */}
                    <div className="o-column o-column--gap-size_400">
                      <label className="t-bold_bold">{t("birth")}: </label>
                      <div className="o-row o-row--gap-size_500">
                        <div>
                          <label>{t("day")}:</label>
                          <CustomInput
                            type="number"
                            value={newStudentFields.fatherDayOfBirth}
                            onChange={(e) =>
                              handleNewStudentInputChange(e, "fatherDayOfBirth")
                            }
                          />
                        </div>
                        <div>
                          <label>{t("month")}:</label>
                          <CustomInput
                            type="number"
                            value={newStudentFields.fatherMonthOfBirth}
                            onChange={(e) =>
                              handleNewStudentInputChange(
                                e,
                                "fatherMonthOfBirth"
                              )
                            }
                          />
                        </div>
                        <div>
                          <label>{t("year")}:</label>
                          <CustomInput
                            type="number"
                            value={newStudentFields.fatherYearOfBirth}
                            onChange={(e) =>
                              handleNewStudentInputChange(
                                e,
                                "fatherYearOfBirth"
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {/* Father Phone */}
                    <div>
                      <label>{t("phoneNumber")}:</label>
                      <CustomInput
                        type="text"
                        value={newStudentFields.fatherPhoneNumber}
                        onChange={(e) =>
                          handleNewStudentInputChange(e, "fatherPhoneNumber")
                        }
                      />
                    </div>

                    {/* Father Education and Job */}
                    <div className="o-row o-row--gap-size_400">
                      <div>
                        <label>{t("education")}:</label>
                        <CustomInput
                          type="text"
                          value={newStudentFields.fatherAcademicAchievement}
                          onChange={(e) =>
                            handleNewStudentInputChange(
                              e,
                              "fatherAcademicAchievement"
                            )
                          }
                        />
                      </div>
                      <div>
                        <label>{t("currentJob")}:</label>
                        <CustomInput
                          type="text"
                          value={newStudentFields.fatherCurrentJob}
                          onChange={(e) =>
                            handleNewStudentInputChange(e, "fatherCurrentJob")
                          }
                        />
                      </div>
                    </div>
                  </section>

                  {/* MOTHER SECTION */}
                  <section className="o-column o-column--gap-size_500">
                    <div>
                      <SectionDivider title={"motherInfo"} />
                      {/* Mother Name */}
                      <div>
                        <label>{t("motherFullName")}:</label>
                        <CustomInput
                          type="text"
                          value={newStudentFields.motherName}
                          onChange={(e) =>
                            handleNewStudentInputChange(e, "motherName")
                          }
                        />
                      </div>
                    </div>

                    {/* Mother Is Alive */}
                    <div>
                      <label>{t("motherIsAlive")}:</label>
                      <Dropdown
                        options={[
                          { title: t("yes"), value: true },
                          { title: t("no"), value: false },
                        ]}
                        selected={{
                          title:
                            newStudentFields.motherAlive === true
                              ? t("yes")
                              : newStudentFields.motherAlive === false
                              ? t("no")
                              : "",
                          value: newStudentFields.motherAlive,
                        }}
                        setSelected={(selected) => {
                          handleNewStudentInputChange(
                            {
                              target: {
                                value: selected.value,
                              },
                            },
                            "motherAlive"
                          );
                        }}
                      />
                    </div>

                    {/* Mother Birthdate */}
                    <div className="o-column o-column--gap-size_400">
                      <label className="t-bold_bold">{t("birth")}: </label>
                      <div className="o-row o-row--gap-size_500">
                        <div>
                          <label>{t("day")}:</label>
                          <CustomInput
                            type="number"
                            value={newStudentFields.motherDayOfBirth}
                            onChange={(e) =>
                              handleNewStudentInputChange(e, "motherDayOfBirth")
                            }
                          />
                        </div>
                        <div>
                          <label>{t("month")}:</label>
                          <CustomInput
                            type="number"
                            value={newStudentFields.motherMonthOfBirth}
                            onChange={(e) =>
                              handleNewStudentInputChange(
                                e,
                                "motherMonthOfBirth"
                              )
                            }
                          />
                        </div>
                        <div>
                          <label>{t("year")}:</label>
                          <CustomInput
                            type="number"
                            value={newStudentFields.motherYearOfBirth}
                            onChange={(e) =>
                              handleNewStudentInputChange(
                                e,
                                "motherYearOfBirth"
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {/* Mother Phone */}
                    <div>
                      <label>{t("phoneNumber")}:</label>
                      <CustomInput
                        type="text"
                        value={newStudentFields.motherPhoneNumber}
                        onChange={(e) =>
                          handleNewStudentInputChange(e, "motherPhoneNumber")
                        }
                      />
                    </div>

                    {/* Mother Education and Job */}
                    <div className="o-row o-row--gap-size_400">
                      <div>
                        <label>{t("education")}:</label>
                        <CustomInput
                          type="text"
                          value={newStudentFields.motherAcademicAchievement}
                          onChange={(e) =>
                            handleNewStudentInputChange(
                              e,
                              "motherAcademicAchievement"
                            )
                          }
                        />
                      </div>
                      <div>
                        <label>{t("currentJob")}:</label>
                        <CustomInput
                          type="text"
                          value={newStudentFields.motherCurrentJob}
                          onChange={(e) =>
                            handleNewStudentInputChange(e, "motherCurrentJob")
                          }
                        />
                      </div>
                    </div>
                  </section>
                </div>
              }
            />
          </div>
        </div>
      </form>

      {showStudentIdModal && (
        <Modal
          title={t("studentId")}
          onClose={handleCloseAllModels}
          modalSize={"c-modal--small"}
          error={errorMessage}
          footer={
            <>
              {loadingModal ? (
                <Loader />
              ) : (
                <section className="o-row o-row--horizontal-align_end o-row--gap-size_200">
                  <CustomButton
                    title={t("close")}
                    onClick={handleCloseAllModels}
                    icon={closeIcon}
                    iconSize={14}
                    variant={"blank"}
                  />
                  <CustomButton
                    title={t("downloadStudentDocument")}
                    onClick={() =>
                      handleDownloadStudentInfo({ id: studentId, studentCode })
                    }
                    icon={downloadIcon}
                    iconSize={16}
                  />
                </section>
              )}
            </>
          }
        >
          <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center o-row--gap-size_400 u-padding_600">
            <CopyButton text={studentCode} />

            <p className="t-headline-1 u-padding_0 u-padding--top_300 u-margin_0">
              {studentCode}
            </p>
          </div>
        </Modal>
      )}
    </Modal>
  );
};

export default AddStudentModal;
